/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListIntegrationsRequest,
  ListIntegrationsSecurity,
} from "../models/operations/listintegrations.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildListIntegrationsQuery,
  ListIntegrationsQueryData,
  prefetchListIntegrations,
  queryKeyListIntegrations,
} from "./listIntegrations.core.js";
export {
  buildListIntegrationsQuery,
  type ListIntegrationsQueryData,
  prefetchListIntegrations,
  queryKeyListIntegrations,
};

export type ListIntegrationsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * list integrations
 *
 * @remarks
 * List available third-party integrations.
 */
export function useListIntegrations(
  request?: ListIntegrationsRequest | undefined,
  security?: ListIntegrationsSecurity | undefined,
  options?: QueryHookOptions<
    ListIntegrationsQueryData,
    ListIntegrationsQueryError
  >,
): UseQueryResult<ListIntegrationsQueryData, ListIntegrationsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildListIntegrationsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * list integrations
 *
 * @remarks
 * List available third-party integrations.
 */
export function useListIntegrationsSuspense(
  request?: ListIntegrationsRequest | undefined,
  security?: ListIntegrationsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ListIntegrationsQueryData,
    ListIntegrationsQueryError
  >,
): UseSuspenseQueryResult<
  ListIntegrationsQueryData,
  ListIntegrationsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildListIntegrationsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setListIntegrationsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      keywords?: Array<string> | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: ListIntegrationsQueryData,
): ListIntegrationsQueryData | undefined {
  const key = queryKeyListIntegrations(...queryKeyBase);

  return client.setQueryData<ListIntegrationsQueryData>(key, data);
}

export function invalidateListIntegrations(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      keywords?: Array<string> | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "integrations", "list", ...queryKeyBase],
  });
}

export function invalidateAllListIntegrations(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "integrations", "list"],
  });
}
